import { z } from 'zod';
import { operatingHoursSchema } from '@vagusx/shared';

/**
 * A front-desk transfer target the WRITE boundary accepts (ADR-198): E.164 or a
 * `sip:` URI. One definition for the API's request schemas and the setup form.
 * The voice side's `normalizeTransferTarget` still fails safe on read, but a
 * value this rejects never reaches the column — a malformed number used to save
 * cleanly, show as configured, and transfer nobody.
 */
export const TRANSFER_TARGET_PATTERN = /^(\+\d{10,15}|sip:.+)$/;

/** What an operator is told when the value cannot route a call. */
export const TRANSFER_TARGET_MESSAGE =
  'Use E.164 (+15551234567) or a sip: address — a formatted or local number cannot be dialed';

export const SPOKEN_ADDRESS_MAX_LENGTH = 300;

/**
 * The spoken address request field (ADR-342): trimmed, whitespace collapsed,
 * blank stored as null so an emptied box follows the address again. The API
 * writes its abbreviations out (`spokenAddress` in `@vagusx/agent-core`) and
 * stores null when the result is the suggestion itself.
 */
export const spokenAddressSchema = z
  .string()
  .transform((text) => text.trim().replace(/\s+/g, ' '))
  .refine((text) => text.length <= SPOKEN_ADDRESS_MAX_LENGTH, {
    message: `Keep it under ${SPOKEN_ADDRESS_MAX_LENGTH} characters`,
  })
  .transform((text) => (text === '' ? null : text));

// Location response from API
export const locationResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  address: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  phone: z.string().nullable().optional(),
  /**
   * Where a caller who asks for a person is bridged to (ADR-198).
   *
   * E.164 (`+16505551234`) or a `sip:` URI. EMPTY IS THE DEFAULT AND A WORKING
   * STATE: with nothing here the agent tells the caller plainly that it cannot
   * connect them and takes a message instead, which is the behaviour every
   * practice gets until it opts in.
   *
   * DELIBERATELY SEPARATE FROM `phone`. The public line frequently answers with
   * the practice's own IVR, and transferring a caller who just asked for a human
   * into an automated menu is worse than the problem it would be solving — so
   * this is never derived from that field.
   *
   * VALIDATED AS A SHAPE ONLY, here and on the way out. A value this schema
   * accepts but `normalizeTransferTarget` rejects resolves to "no transfer",
   * which fails safe; the two are kept loose-then-strict rather than duplicating
   * one regex across the wire.
   */
  transferPhone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  fax: z.string().nullable().optional(),
  googleMapsUrl: z.string().nullable().optional(),
  /**
   * The address as the voice agent says it, written by the practice (ADR-342);
   * null when it follows the address, i.e. the agent says `spokenUsAddress` of
   * the parts ("18095 Highway 18, Apple Valley, California, 9 2 3 0 7"). Said
   * only when a caller asks where the office is.
   */
  customSpokenAddress: z.string().nullable().optional(),
  hours: operatingHoursSchema.nullable().optional(),
  timezone: z.string().nullable().optional(),
  isVirtual: z.boolean().optional(),
  rowStatus: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type LocationResponse = z.infer<typeof locationResponseSchema>;

// Request schemas — the API's routes validate against these, so the form and
// the server share one set of rules and error messages.

// Create location request
export const createLocationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  address: z.string().min(1, 'Address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zip: z.string().min(1, 'ZIP code is required'),
  phone: z.string().optional(),
  /** See `locationResponseSchema.transferPhone`. */
  transferPhone: z.string().regex(TRANSFER_TARGET_PATTERN, TRANSFER_TARGET_MESSAGE).optional(),
  email: z.string().email().optional(),
  fax: z.string().optional(),
  googleMapsUrl: z.string().url().optional(),
  /** See `locationResponseSchema.customSpokenAddress`. Blank or the suggestion follows the address. */
  spokenAddress: spokenAddressSchema.optional(),
  hours: operatingHoursSchema.optional(),
  timezone: z.string().min(1, 'Timezone is required'),
  isVirtual: z.boolean().optional(),
});

export type CreateLocationRequest = z.infer<typeof createLocationSchema>;

// Update location request
export const updateLocationSchema = z.object({
  name: z.string().min(1).optional(),
  address: z.string().min(1).optional(),
  city: z.string().min(1).optional(),
  state: z.string().min(1).optional(),
  zip: z.string().min(1).optional(),
  phone: z.string().nullable().optional(),
  /** See `locationResponseSchema.transferPhone`. Null clears it and restores the message path. */
  transferPhone: z
    .string()
    .regex(TRANSFER_TARGET_PATTERN, TRANSFER_TARGET_MESSAGE)
    .nullable()
    .optional(),
  email: z.string().email().nullable().optional(),
  fax: z.string().nullable().optional(),
  googleMapsUrl: z.string().url().nullable().optional(),
  /** See `locationResponseSchema.customSpokenAddress`. Null or blank follows the address. */
  spokenAddress: spokenAddressSchema.nullable().optional(),
  hours: operatingHoursSchema.nullable().optional(),
  // NOT NULL in the table: an update can change it, never clear it.
  timezone: z.string().min(1).optional(),
  isVirtual: z.boolean().optional(),
});

export type UpdateLocationRequest = z.infer<typeof updateLocationSchema>;
